import type { SyntheticEvent } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import {
	COPY_PAGE_LEGACY,
	CREATE_PAGE_ACTION_LEGACY,
	EXTERNAL_SHARE,
} from '@confluence/named-routes';

import type { PreloadRouteOptions } from './RouteState';
import type { RoutesContextType } from './RoutesContext';
import { RoutesContext } from './RoutesContext';

declare global {
	interface WindowEventMap {
		preloadOnHover: CustomEvent;
	}
}

type PreloadRouteOptionsWithHref = PreloadRouteOptions & { href: string };

export function GlobalAnchorHandler() {
	useAnchorEventListener('click', spaTransitionOnAnchorClick, false);

	useAnchorEventListener('mousedown', preloadRouteOnMouseDown, true);

	useAnchorEventListener('preloadOnHover', preloadRouteOnMouseDown, true);

	return null;
}

function createAnchorEventListener(
	eventListener: (
		e: MouseEvent | CustomEvent,
		routesContext: RoutesContextType,
		preloadOptions: PreloadRouteOptionsWithHref,
	) => void,
	routesContext: RoutesContextType,
) {
	return (e: MouseEvent | CustomEvent) => {
		// Some code already handled this
		if (e.defaultPrevented) return;

		const details = getSpaTransitionDetails(e, routesContext);
		if (details) {
			eventListener(e, routesContext, details);
		}
	};
}

function isContentEditable(element: Element | null): boolean {
	return element instanceof HTMLElement && element.isContentEditable;
}

export function getSpaTransitionDetails(
	e: MouseEvent | SyntheticEvent<HTMLAnchorElement> | CustomEvent,
	routesContext: Pick<RoutesContextType, 'matchRoute'>,
): PreloadRouteOptionsWithHref | undefined {
	if (e.type === 'preloadOnHover') {
		const detail = (e as CustomEvent).detail;
		if (detail?.preloadUrl) {
			return {
				href: detail.preloadUrl,
				onlyJS: detail.onlyJS ?? false,
			};
		}

		return;
	}
	if (!('button' in e) || e['button'] !== 0 /* main button */) return;
	// Don't handle if modifier keys used
	if (
		(e as MouseEvent)['ctrlKey'] ||
		(e as MouseEvent)['shiftKey'] ||
		(e as MouseEvent)['altKey'] ||
		(e as MouseEvent)['metaKey']
	)
		return;

	const { target } = e;
	if (!target || !(target instanceof Element)) return;

	// This might be the child of an anchor (such as the span inside of a smart card),
	// so check for a parent anchor.
	const anchor = target.closest('a');
	if (!anchor) return;

	//ignore hash links, they're handled by scroll handlers that also listen to document click
	if (!(anchor instanceof HTMLAnchorElement) || ('hash' in anchor && anchor['hash'])) {
		return;
	}

	// Links in contenteditable elements are not clickable
	// https://product-fabric.atlassian.net/browse/CEMS-951
	// Checking closest 'contentEditable' for anchor,
	// In case there are "contenteditable=false" in between target and anchor.
	// Also check the property, since contenteditable="false" is possible
	const closestContentEditableFromAnchor = anchor.closest('[contenteditable]');
	const closestContentEditableFromTarget = target.closest('[contenteditable]');
	if (
		isContentEditable(closestContentEditableFromAnchor) ||
		isContentEditable(closestContentEditableFromTarget)
	) {
		return;
	}

	const noGlobalAnchorHandler = target.closest('[data-no-global-anchor-handler]');
	if (noGlobalAnchorHandler) return;

	// Only handle anchors with relevant targets
	if (anchor.target && anchor.target !== '_self') return;

	const { href } = anchor;
	if (!href) return;

	// CONFCLOUD-69367
	// skip copy page legacy to allow user to edit page title
	// CEMS-1140
	// skip create page legacy to allow user to create a page using newer working fabric creation
	if (!routesContext || typeof routesContext.matchRoute !== 'function') return;
	const match = routesContext.matchRoute(href);
	if (
		match &&
		(match.name === COPY_PAGE_LEGACY.name || match.name === CREATE_PAGE_ACTION_LEGACY.name)
	) {
		return;
	}

	return { href, onlyJS: false };
}

function preloadRouteOnMouseDown(
	_: Event,
	{ preloadRoute }: RoutesContextType,
	{ href, onlyJS }: PreloadRouteOptionsWithHref,
) {
	preloadRoute(href, { onlyJS });
}

function spaTransitionOnAnchorClick(
	e: Event | CustomEvent,
	{ push }: RoutesContextType,
	{ href }: PreloadRouteOptionsWithHref,
) {
	e.preventDefault();
	push(href, Boolean(EXTERNAL_SHARE.match(window.location.pathname)));
}

function useAnchorEventListener(
	type: 'click' | 'mousedown' | 'preloadOnHover',
	anchorEventListener: Parameters<typeof createAnchorEventListener>[0],
	useCapture: boolean,
) {
	const routesContext = useContext(RoutesContext);
	const eventListener = useMemo(
		() => createAnchorEventListener(anchorEventListener, routesContext),
		[anchorEventListener, routesContext],
	);

	useEffect(() => {
		if (eventListener) {
			window.addEventListener(type, eventListener, useCapture);
		}

		return () => {
			if (eventListener) {
				window.removeEventListener(type, eventListener, useCapture);
			}
		};
	}, [eventListener, type, useCapture]);
}
